import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, Modal, TextInput, TouchableOpacity, ScrollView, Platform, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import InlineMessage from './InlineMessage';
import DateTimePicker from './DateTimePicker';
import { apiPatch, apiGet } from '../api/client';
import { useTheme } from '../context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../utils/theme';
import type { ColorTheme } from '../utils/theme';
import { useModalBottomPadding } from '../utils/useModalBottomPadding';
import { titleCase } from '../utils/format';

/** A single shift record as exposed by the API. */
export interface ShiftLike {
  id: string;
  attendantName?: string | null;
  locationName?: string | null;
  clockInTime: string | Date;
  clockOutTime: string | Date | null;
  notes?: string | null;
  status?: string;
  lockedAt?: string | Date | null;
}

interface AuditEntry {
  id: string;
  editorOwnerId: string | null;
  editorAttendantId: string | null;
  oldClockIn: string | null;
  newClockIn: string | null;
  oldClockOut: string | null;
  newClockOut: string | null;
  oldNotes: string | null;
  newNotes: string | null;
  reason: string;
  createdAt: string;
}

interface EditShiftModalProps {
  readonly visible: boolean;
  readonly shift: ShiftLike | null;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}

/** Calendar + time picker for a clock in/out value. Greys out + blocks
 *  interaction when disabled (locked period, saving, or still clocked in).
 *  Defined at module level — defining it inside the parent's render gives it a
 *  new identity every render, which remounts DateTimePicker and resets its
 *  internal open state (the picker modal closed on every date/time tap). */
const DateTimeField = ({ value, onChange, disabled }: { value: string; onChange: (v: string) => void; disabled?: boolean }) => (
  <View style={disabled ? { opacity: 0.5 } : undefined} pointerEvents={disabled ? 'none' : 'auto'}>
    <DateTimePicker value={value} onChange={onChange} />
  </View>
);

/** Convert a Date or ISO string to the format datetime-local inputs expect. */
function toLocalInput(d: string | Date | null | undefined): string {
  if (!d) return '';
  const dt = typeof d === 'string' ? new Date(d) : d;
  if (isNaN(dt.getTime())) return '';
  // YYYY-MM-DDTHH:MM in local time
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

function fromLocalInput(s: string): string | null {
  if (!s) return null;
  const dt = new Date(s);
  if (isNaN(dt.getTime())) return null;
  return dt.toISOString();
}

export default function EditShiftModal({ visible, shift, onClose, onSaved }: EditShiftModalProps) {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const modalBottomPadding = useModalBottomPadding();

  const [clockIn, setClockIn] = useState('');
  const [clockOut, setClockOut] = useState('');
  const [stillClockedIn, setStillClockedIn] = useState(false);
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [audit, setAudit] = useState<AuditEntry[] | null>(null);
  const [auditLoading, setAuditLoading] = useState(false);

  useEffect(() => {
    if (!visible || !shift) return;
    setClockIn(toLocalInput(shift.clockInTime));
    setStillClockedIn(!shift.clockOutTime);
    setClockOut(toLocalInput(shift.clockOutTime));
    setNotes(shift.notes || '');
    setReason('');
    setErrorMsg(null);
    setAudit(null);
  }, [visible, shift?.id]);

  const loadAudit = async () => {
    if (!shift) return;
    setAuditLoading(true);
    try {
      const data = await apiGet<{ edits: AuditEntry[] }>(`/api/shifts/${shift.id}/edits`);
      setAudit(data.edits);
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Failed to load audit');
    } finally {
      setAuditLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!shift) return;
    setErrorMsg(null);
    if (reason.trim().length < 3) {
      setErrorMsg('Please enter a reason for the edit (at least 3 characters).');
      return;
    }
    const clockInIso = fromLocalInput(clockIn);
    if (!clockInIso) {
      setErrorMsg('Clock-in time is invalid.');
      return;
    }
    const clockOutIso = stillClockedIn ? null : fromLocalInput(clockOut);
    if (!stillClockedIn && !clockOutIso) {
      setErrorMsg('Clock-out time is invalid.');
      return;
    }
    setSaving(true);
    try {
      await apiPatch(`/api/shifts/${shift.id}`, {
        clockInTime: clockInIso,
        clockOutTime: clockOutIso,
        notes: notes || null,
        reason: reason.trim(),
      });
      onSaved();
      onClose();
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!shift) return null;
  const locked = !!shift.lockedAt;

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <ScrollView style={[styles.content, { marginBottom: modalBottomPadding }]} contentContainerStyle={{ padding: spacing.lg }}>
          <Text style={styles.title}>Edit Timecard</Text>
          {shift.attendantName && <Text style={styles.subtitle}>{titleCase(shift.attendantName)}{shift.locationName ? ` · ${shift.locationName}` : ''}</Text>}

          {locked && (
            <InlineMessage type="warning" message="This shift is in a locked pay period. Unlock the period before editing." visible={true} onDismiss={() => {}} />
          )}

          <Text style={styles.label}>Clock In</Text>
          <DateTimeField value={clockIn} onChange={setClockIn} disabled={locked || saving} />

          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: spacing.md }}>
            <Text style={styles.label}>Clock Out</Text>
            <TouchableOpacity onPress={() => setStillClockedIn(v => !v)} disabled={locked || saving} style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <Ionicons name={stillClockedIn ? 'checkbox' : 'square-outline'} size={18} color={colors.primary} />
              <Text style={{ color: colors.primary, fontSize: fontSize.xs }}>Still clocked in</Text>
            </TouchableOpacity>
          </View>
          <DateTimeField value={clockOut} onChange={setClockOut} disabled={locked || saving || stillClockedIn} />

          <Text style={styles.label}>Notes</Text>
          <TextInput
            style={[styles.input, { height: 70 }]}
            value={notes}
            onChangeText={setNotes}
            multiline
            editable={!locked && !saving}
            placeholder="Optional shift notes"
          />

          <Text style={styles.label}>Reason for edit (required)</Text>
          <TextInput
            style={styles.input}
            value={reason}
            onChangeText={setReason}
            editable={!locked && !saving}
            placeholder="e.g. forgot to clock out at end of shift"
            maxLength={500}
          />

          {errorMsg && (
            <InlineMessage type="error" message={errorMsg} visible={true} onDismiss={() => setErrorMsg(null)} />
          )}

          <View style={styles.actions}>
            <TouchableOpacity style={styles.cancelBtn} onPress={onClose} disabled={saving}>
              <Text style={styles.cancelText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.saveBtn, (locked || saving) && { opacity: 0.5 }]}
              onPress={handleSubmit}
              disabled={locked || saving}
            >
              <Text style={styles.saveText}>{saving ? 'Saving...' : 'Save Edit'}</Text>
            </TouchableOpacity>
          </View>

          <View style={{ height: spacing.md }} />
          <TouchableOpacity onPress={loadAudit} style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <Ionicons name="time-outline" size={16} color={colors.primary} />
            <Text style={{ color: colors.primary, fontSize: fontSize.sm }}>
              {audit ? 'Refresh audit history' : 'View audit history'}
            </Text>
          </TouchableOpacity>

          {auditLoading && <ActivityIndicator color={colors.primary} style={{ marginTop: spacing.sm }} />}

          {audit && (
            <View style={{ marginTop: spacing.sm }}>
              {audit.length === 0 ? (
                <Text style={{ fontSize: fontSize.xs, color: colors.textLight }}>No prior edits.</Text>
              ) : (
                audit.map(a => (
                  <View key={a.id} style={styles.auditRow}>
                    <Text style={styles.auditTime}>{new Date(a.createdAt).toLocaleString()}</Text>
                    <Text style={styles.auditReason}>{a.reason}</Text>
                    <Text style={styles.auditDelta}>
                      {a.oldClockIn !== a.newClockIn && `clock-in: ${a.oldClockIn} → ${a.newClockIn}\n`}
                      {a.oldClockOut !== a.newClockOut && `clock-out: ${a.oldClockOut} → ${a.newClockOut}\n`}
                      {a.oldNotes !== a.newNotes && `notes: ${a.oldNotes || '∅'} → ${a.newNotes || '∅'}`}
                    </Text>
                  </View>
                ))
              )}
            </View>
          )}
        </ScrollView>
      </View>
    </Modal>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center', padding: spacing.md },
  content: { backgroundColor: c.surface, borderRadius: borderRadius.lg, width: '100%', maxWidth: 520, maxHeight: '90%' },
  title: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text },
  subtitle: { fontSize: fontSize.sm, color: c.textSecondary, marginBottom: spacing.md },
  label: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text, marginTop: spacing.sm, marginBottom: 4 },
  input: {
    backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.sm, padding: spacing.sm,
    fontSize: fontSize.sm, color: c.text, borderWidth: 1, borderColor: c.border,
  },
  actions: { flexDirection: 'row', justifyContent: 'flex-end', gap: spacing.sm, marginTop: spacing.md },
  cancelBtn: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.sm },
  cancelText: { fontSize: fontSize.sm, color: c.textSecondary, fontWeight: fontWeight.medium },
  saveBtn: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.sm, backgroundColor: c.primary },
  saveText: { fontSize: fontSize.sm, color: c.white, fontWeight: fontWeight.semibold },
  auditRow: { borderTopWidth: 1, borderTopColor: c.borderLight, paddingTop: spacing.xs, marginTop: spacing.xs },
  auditTime: { fontSize: fontSize.xs, color: c.textLight },
  auditReason: { fontSize: fontSize.sm, color: c.text, fontWeight: fontWeight.medium, marginTop: 2 },
  auditDelta: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2, fontFamily: Platform.OS === 'web' ? 'monospace' : undefined },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
